import { useMemo } from 'react';

interface ParticleConfig {
  emojis: string[];
  density: number;
  speed: 'slow' | 'medium' | 'fast';
  emojiScales?: Record<string, number>;
}

interface Props {
  config: ParticleConfig;
  themeId: string;
}

const SPEED_DURATIONS = {
  slow: { min: 20, max: 35 },
  medium: { min: 14, max: 24 },
  fast: { min: 8, max: 16 },
};

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

interface Particle {
  id: number;
  emoji: string;
  left: number;
  duration: number;
  delay: number;
  scale: number;
  driftX: number;
  driftRotate: number;
  opacity: number;
  layer: number;
}

// ── Dino ──────────────────────────────────────────────────────────────────────

function DinoScene() {
  return (
    <>
      <div className="theme-dino-scene">
        <svg viewBox="0 0 1440 280" preserveAspectRatio="none" width="100%" height="100%">
          <defs>
            <linearGradient id="dino-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fef3c7" stopOpacity="0" />
              <stop offset="65%" stopColor="#fde68a" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.42" />
            </linearGradient>
            <radialGradient id="dino-sun-halo" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.50" />
              <stop offset="55%" stopColor="#f59e0b" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Sky wash */}
          <rect x="0" y="0" width="1440" height="280" fill="url(#dino-sky)" />

          {/* Sun disc at horizon */}
          <circle cx="920" cy="228" r="90" fill="url(#dino-sun-halo)" />
          <circle cx="920" cy="228" r="55" fill="#fbbf24" fillOpacity="0.32" />

          {/* Distant mountain range */}
          <path d="M0 218 Q140 170 280 205 Q420 240 560 188 Q700 136 840 182 Q980 228 1120 168 Q1260 108 1440 178 L1440 280 L0 280 Z"
            fill="#78350f" fillOpacity="0.20" />

          {/* Background volcano — far left, smaller, muted */}
          <path d="M-60 280 L58 178 Q67 168 76 173 Q85 168 94 178 L212 280 Z"
            fill="#3d1200" fillOpacity="0.55" />
          <ellipse cx="76" cy="171" rx="11" ry="5" fill="#ea580c" fillOpacity="0.45" className="dino-lava-glow" />
          <circle cx="76" cy="154" r="9"  fill="#78350f" fillOpacity="0.22" className="dino-smoke dino-smoke-b" />
          <circle cx="70" cy="138" r="7"  fill="#78350f" fillOpacity="0.15" className="dino-smoke dino-smoke-c" />

          {/* Cliff — right side, rocky mesa */}
          <path d="M1180 280 L1192 200 Q1198 188 1210 192 L1240 192 Q1252 190 1260 200 L1270 220 Q1278 188 1290 182 Q1302 176 1314 185 L1330 200 L1340 280 Z"
            fill="#1c0a00" fillOpacity="0.78" />
          {/* Cliff rock texture lines */}
          <line x1="1210" y1="210" x2="1200" y2="240" stroke="#3d1200" strokeWidth="1.5" strokeOpacity="0.5" />
          <line x1="1250" y1="198" x2="1242" y2="235" stroke="#3d1200" strokeWidth="1.5" strokeOpacity="0.5" />
          <line x1="1300" y1="192" x2="1292" y2="230" stroke="#3d1200" strokeWidth="1.5" strokeOpacity="0.5" />

          {/* Palm tree — right side, atop cliff */}
          {/* trunk */}
          <path d="M1258 192 Q1261 160 1255 128 Q1253 110 1260 95"
            stroke="#5c3a0a" strokeWidth="7" fill="none" strokeLinecap="round" />
          {/* fronds */}
          <path d="M1260 95 Q1230 70 1210 65" stroke="#365314" strokeWidth="4" fill="none" strokeLinecap="round" strokeOpacity="0.9"/>
          <path d="M1260 95 Q1240 58 1248 42" stroke="#365314" strokeWidth="4" fill="none" strokeLinecap="round" strokeOpacity="0.9"/>
          <path d="M1260 95 Q1265 55 1278 45" stroke="#365314" strokeWidth="4" fill="none" strokeLinecap="round" strokeOpacity="0.9"/>
          <path d="M1260 95 Q1282 68 1300 68" stroke="#365314" strokeWidth="4" fill="none" strokeLinecap="round" strokeOpacity="0.9"/>
          <path d="M1260 95 Q1288 82 1314 85" stroke="#365314" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeOpacity="0.75"/>
          <path d="M1260 95 Q1228 84 1208 90" stroke="#365314" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeOpacity="0.75"/>
          {/* coconuts */}
          <circle cx="1252" cy="100" r="5" fill="#92400e" fillOpacity="0.80" />
          <circle cx="1261" cy="97"  r="4" fill="#78350f" fillOpacity="0.80" />

          {/* Volcano */}
          <path d="M230 280 L368 142 Q380 128 392 134 Q404 128 416 142 L554 280 Z"
            fill="#1c0a00" fillOpacity="0.88" />
          {/* Lava crater */}
          <ellipse cx="392" cy="137" rx="20" ry="9" fill="#f97316" className="dino-lava-glow" />
          {/* Lava drip */}
          <path d="M387 140 Q384 162 389 180 Q385 196 391 208"
            stroke="#f97316" strokeWidth="3" fill="none" strokeLinecap="round" className="dino-lava-drip" />
          {/* Smoke puffs */}
          <circle cx="392" cy="112" r="14" fill="#92400e" fillOpacity="0.28" className="dino-smoke dino-smoke-a" />
          <circle cx="384" cy="92"  r="11" fill="#92400e" fillOpacity="0.20" className="dino-smoke dino-smoke-b" />
          <circle cx="398" cy="74"  r="8"  fill="#92400e" fillOpacity="0.14" className="dino-smoke dino-smoke-c" />

          {/* Far ferns LEFT — mid-size, muted green */}
          <g fill="#365314" fillOpacity="0.52">
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(-50 55 265)" />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(-28 55 265)" />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(-6 55 265)"  />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(16 55 265)"  />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(38 55 265)"  />
            <rect x="51" y="238" width="9" height="38" rx="3" />
          </g>
          {/* Far ferns RIGHT — mirrored */}
          <g fill="#365314" fillOpacity="0.52" transform="translate(1440 0) scale(-1 1)">
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(-50 55 265)" />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(-28 55 265)" />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(-6 55 265)"  />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(16 55 265)"  />
            <ellipse cx="55" cy="265" rx="40" ry="11" transform="rotate(38 55 265)"  />
            <rect x="51" y="238" width="9" height="38" rx="3" />
          </g>

          {/* Near ferns LEFT — large, dark, foreground */}
          <g fill="#14532d" fillOpacity="0.85">
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(-48 -5 278)" />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(-26 -5 278)" />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(-4 -5 278)"  />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(18 -5 278)"  />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(40 -5 278)"  />
            <rect x="-10" y="245" width="10" height="42" rx="4" />
          </g>
          {/* Near ferns RIGHT — mirrored */}
          <g fill="#14532d" fillOpacity="0.85" transform="translate(1445 0) scale(-1 1)">
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(-48 -5 278)" />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(-26 -5 278)" />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(-4 -5 278)"  />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(18 -5 278)"  />
            <ellipse cx="-5" cy="278" rx="58" ry="15" transform="rotate(40 -5 278)"  />
            <rect x="-10" y="245" width="10" height="42" rx="4" />
          </g>

          {/* Ground */}
          <path d="M0 258 Q360 244 720 252 Q1080 260 1440 248 L1440 280 L0 280 Z"
            fill="#1c0a00" fillOpacity="0.62" />
        </svg>
      </div>
      <div className="theme-volcanic-glow" />
    </>
  );
}

// ── Ocean ──────────────────────────────────────────────────────────────────────

function OceanScene() {
  return (
    <>
      <div className="theme-ocean-caustics" />
      <div className="theme-ocean-scene">
        <svg viewBox="0 0 1440 240" preserveAspectRatio="none" width="100%" height="100%">
          <defs>
            <linearGradient id="ocean-depth" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#071828" stopOpacity="0" />
              <stop offset="100%" stopColor="#0a2a45" stopOpacity="0.60" />
            </linearGradient>
          </defs>

          <rect x="0" y="0" width="1440" height="240" fill="url(#ocean-depth)" />

          {/* Seafloor */}
          <path d="M0 212 Q360 196 720 206 Q1080 216 1440 202 L1440 240 L0 240 Z"
            fill="#071828" fillOpacity="0.70" />

          {/* Distant fish silhouettes */}
          <g fill="#0369a1" fillOpacity="0.25">
            <ellipse cx="640" cy="175" rx="15" ry="6" />
            <path d="M625 175 L614 171 L614 179 Z" />
          </g>
          <g fill="#0369a1" fillOpacity="0.18">
            <ellipse cx="760" cy="148" rx="11" ry="4" />
            <path d="M749 148 L741 145 L741 151 Z" />
          </g>
          <g fill="#0369a1" fillOpacity="0.14">
            <ellipse cx="580" cy="128" rx="8" ry="3" />
            <path d="M572 128 L566 126 L566 130 Z" />
          </g>
          <g fill="#0891b2" fillOpacity="0.20">
            <ellipse cx="860" cy="162" rx="12" ry="5" />
            <path d="M872 162 L881 159 L881 165 Z" />
          </g>

          {/* Kelp columns LEFT */}
          <g className="kelp-col-1" style={{ transformOrigin: '55px 240px' }}>
            <path d="M55 240 Q47 198 57 155 Q49 112 59 68 Q51 28 61 0"
              stroke="#0f766e" strokeWidth="5" fill="none" strokeLinecap="round" />
            <ellipse cx="62" cy="138" rx="23" ry="8" fill="#0f766e" fillOpacity="0.68" transform="rotate(-35 62 138)" />
            <ellipse cx="54" cy="85"  rx="21" ry="7" fill="#0f766e" fillOpacity="0.58" transform="rotate(30 54 85)"  />
            <ellipse cx="63" cy="38"  rx="18" ry="6" fill="#0f766e" fillOpacity="0.50" transform="rotate(-22 63 38)"  />
          </g>
          <g className="kelp-col-2" style={{ transformOrigin: '92px 240px' }}>
            <path d="M92 240 Q85 202 95 162 Q87 122 97 82 Q89 44 99 8"
              stroke="#115e59" strokeWidth="4" fill="none" strokeLinecap="round" />
            <ellipse cx="99" cy="145" rx="19" ry="6" fill="#115e59" fillOpacity="0.62" transform="rotate(32 99 145)" />
            <ellipse cx="93" cy="94"  rx="17" ry="6" fill="#115e59" fillOpacity="0.52" transform="rotate(-26 93 94)"  />
          </g>
          <g className="kelp-col-3" style={{ transformOrigin: '126px 240px' }}>
            <path d="M126 240 Q118 200 128 158 Q120 116 130 72"
              stroke="#0d9488" strokeWidth="4" fill="none" strokeLinecap="round" />
            <ellipse cx="132" cy="130" rx="20" ry="6" fill="#0d9488" fillOpacity="0.58" transform="rotate(-28 132 130)" />
            <ellipse cx="126" cy="80"  rx="16" ry="5" fill="#0d9488" fillOpacity="0.48" transform="rotate(22 126 80)"  />
          </g>

          {/* Kelp columns RIGHT */}
          <g className="kelp-col-4" style={{ transformOrigin: '1385px 240px' }}>
            <path d="M1385 240 Q1393 198 1383 155 Q1391 112 1381 68 Q1389 28 1379 0"
              stroke="#0f766e" strokeWidth="5" fill="none" strokeLinecap="round" />
            <ellipse cx="1378" cy="138" rx="23" ry="8" fill="#0f766e" fillOpacity="0.68" transform="rotate(35 1378 138)"  />
            <ellipse cx="1386" cy="85"  rx="21" ry="7" fill="#0f766e" fillOpacity="0.58" transform="rotate(-30 1386 85)"  />
            <ellipse cx="1377" cy="38"  rx="18" ry="6" fill="#0f766e" fillOpacity="0.50" transform="rotate(22 1377 38)"   />
          </g>
          <g className="kelp-col-5" style={{ transformOrigin: '1348px 240px' }}>
            <path d="M1348 240 Q1355 202 1345 162 Q1353 122 1343 82 Q1351 44 1341 8"
              stroke="#115e59" strokeWidth="4" fill="none" strokeLinecap="round" />
            <ellipse cx="1341" cy="145" rx="19" ry="6" fill="#115e59" fillOpacity="0.62" transform="rotate(-32 1341 145)" />
            <ellipse cx="1347" cy="94"  rx="17" ry="6" fill="#115e59" fillOpacity="0.52" transform="rotate(26 1347 94)"   />
          </g>

          {/* Coral reef LEFT */}
          <g stroke="#0e7490" strokeWidth="3.5" fill="none" strokeLinecap="round">
            <path d="M195 240 L195 206 L182 182 M195 206 L208 182" />
            <path d="M182 182 L174 165 M182 182 L190 165" />
            <path d="M208 182 L200 165 M208 182 L218 165" />
          </g>
          <g stroke="#0891b2" strokeWidth="2.5" fill="none" strokeLinecap="round">
            <path d="M244 240 L244 210 L234 190 M244 210 L254 190" />
            <path d="M234 190 L228 176 M234 190 L240 176" />
            <path d="M254 190 L248 176 M254 190 L262 176" />
          </g>
          <g fill="#06b6d4" fillOpacity="0.55">
            <circle cx="166" cy="234" r="5.5" /><circle cx="175" cy="229" r="4"   />
            <circle cx="158" cy="231" r="4"   /><circle cx="171" cy="225" r="3.5" />
            <circle cx="163" cy="226" r="3"   />
          </g>
          <g fill="#0891b2" fillOpacity="0.50">
            <circle cx="286" cy="236" r="4.5" /><circle cx="294" cy="231" r="3.5" />
            <circle cx="278" cy="233" r="3.5" /><circle cx="290" cy="227" r="3"   />
          </g>

          {/* Coral reef RIGHT */}
          <g stroke="#0e7490" strokeWidth="3.5" fill="none" strokeLinecap="round">
            <path d="M1245 240 L1245 206 L1232 182 M1245 206 L1258 182" />
            <path d="M1232 182 L1224 165 M1232 182 L1240 165" />
            <path d="M1258 182 L1250 165 M1258 182 L1268 165" />
          </g>
          <g stroke="#0891b2" strokeWidth="2.5" fill="none" strokeLinecap="round">
            <path d="M1196 240 L1196 210 L1186 190 M1196 210 L1206 190" />
            <path d="M1186 190 L1180 176 M1186 190 L1192 176" />
            <path d="M1206 190 L1200 176 M1206 190 L1214 176" />
          </g>
          <g fill="#06b6d4" fillOpacity="0.55">
            <circle cx="1274" cy="234" r="5.5" /><circle cx="1283" cy="229" r="4"   />
            <circle cx="1266" cy="231" r="4"   /><circle cx="1279" cy="225" r="3.5" />
          </g>
          <g fill="#0891b2" fillOpacity="0.50">
            <circle cx="1154" cy="236" r="4.5" /><circle cx="1162" cy="231" r="3.5" />
            <circle cx="1146" cy="233" r="3.5" /><circle cx="1158" cy="227" r="3"   />
          </g>
        </svg>
      </div>
      <div className="theme-ocean-shimmer" />
    </>
  );
}

// ── Space Station ──────────────────────────────────────────────────────────────

function SpaceStarfield({ seed }: { seed: number }) {
  const boxShadow = useMemo(() => {
    return Array.from({ length: 130 }, (_, i) => {
      const x = Math.round(seededRandom(seed + i * 7)  * 1440);
      const y = Math.round(seededRandom(seed + i * 13) * 900);
      const a = (0.20 + seededRandom(seed + i * 19) * 0.70).toFixed(2);
      return `${x}px ${y}px 0 0 rgba(255,255,255,${a})`;
    }).join(',');
  }, [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[0]">
      <div style={{ position: 'absolute', width: '1px', height: '1px',
        background: 'rgba(255,255,255,0.9)', boxShadow }} />
    </div>
  );
}

function SpaceScene() {
  return (
    <>
      <div className="theme-space-planet" />
      <div className="theme-space-scene">
        <svg viewBox="0 0 1440 260" preserveAspectRatio="none" width="100%" height="100%">
          <defs>
            <radialGradient id="nebula-a" cx="20%" cy="40%" r="50%">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="nebula-b" cx="80%" cy="30%" r="40%">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="nebula-c" cx="50%" cy="15%" r="35%">
              <stop offset="0%" stopColor="#db2777" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#db2777" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Nebula washes */}
          <rect x="0" y="0" width="1440" height="260" fill="url(#nebula-a)" className="space-nebula-a" />
          <rect x="0" y="0" width="1440" height="260" fill="url(#nebula-b)" className="space-nebula-b" />
          <rect x="0" y="0" width="1440" height="260" fill="url(#nebula-c)" className="space-nebula-c" />
          {/* Constellation dots */}
          <circle cx="180" cy="60"  r="2"   fill="#e9d5ff" fillOpacity="0.80" />
          <circle cx="210" cy="44"  r="1.5" fill="#e9d5ff" fillOpacity="0.70" />
          <circle cx="240" cy="68"  r="2"   fill="#e9d5ff" fillOpacity="0.75" />
          <circle cx="198" cy="80"  r="1.5" fill="#e9d5ff" fillOpacity="0.65" />
          <line x1="180" y1="60" x2="210" y2="44"  stroke="#e9d5ff" strokeWidth="0.5" strokeOpacity="0.30" />
          <line x1="210" y1="44" x2="240" y2="68"  stroke="#e9d5ff" strokeWidth="0.5" strokeOpacity="0.30" />
          <line x1="240" y1="68" x2="198" y2="80"  stroke="#e9d5ff" strokeWidth="0.5" strokeOpacity="0.30" />
          <line x1="198" y1="80" x2="180" y2="60"  stroke="#e9d5ff" strokeWidth="0.5" strokeOpacity="0.30" />

          <circle cx="1260" cy="50"  r="2"   fill="#bfdbfe" fillOpacity="0.75" />
          <circle cx="1295" cy="35"  r="1.5" fill="#bfdbfe" fillOpacity="0.70" />
          <circle cx="1318" cy="58"  r="2"   fill="#bfdbfe" fillOpacity="0.80" />
          <circle cx="1282" cy="72"  r="1.5" fill="#bfdbfe" fillOpacity="0.65" />
          <line x1="1260" y1="50" x2="1295" y2="35"  stroke="#bfdbfe" strokeWidth="0.5" strokeOpacity="0.28" />
          <line x1="1295" y1="35" x2="1318" y2="58"  stroke="#bfdbfe" strokeWidth="0.5" strokeOpacity="0.28" />
          <line x1="1318" y1="58" x2="1282" y2="72"  stroke="#bfdbfe" strokeWidth="0.5" strokeOpacity="0.28" />
        </svg>
      </div>
    </>
  );
}

// ── Safari ──────────────────────────────────────────────────────────────────

function GiraffeSVG() {
  return (
    <svg width="55" height="80" viewBox="0 0 55 80" fill="#78350f">
      <ellipse cx="24" cy="60" rx="16" ry="11" />
      <rect x="28" y="24" width="9" height="38" rx="4" />
      <ellipse cx="34" cy="20" rx="7" ry="5" />
      <rect x="30" y="12" width="2" height="8" rx="1" />
      <rect x="36" y="12" width="2" height="7" rx="1" />
      <rect x="10" y="68" width="5" height="12" rx="2" />
      <rect x="18" y="68" width="5" height="12" rx="2" />
      <rect x="28" y="68" width="5" height="12" rx="2" />
      <rect x="36" y="68" width="5" height="12" rx="2" />
    </svg>
  );
}

function ElephantSVG() {
  return (
    <svg width="85" height="60" viewBox="0 0 85 60" fill="#6b7280">
      <ellipse cx="48" cy="38" rx="30" ry="18" />
      <circle cx="18" cy="28" r="13" />
      <ellipse cx="10" cy="24" rx="9" ry="12" fillOpacity="0.7" />
      <path d="M10 34 Q1 44 6 54" stroke="#6b7280" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M12 36 Q4 43 8 50" stroke="#d6d3d1" strokeWidth="2" fill="none" strokeLinecap="round" />
      <rect x="26" y="52" width="9" height="8" rx="3" />
      <rect x="40" y="52" width="9" height="8" rx="3" />
      <rect x="54" y="52" width="9" height="8" rx="3" />
      <rect x="66" y="52" width="9" height="8" rx="3" />
    </svg>
  );
}

function ZebraSVG() {
  return (
    <svg width="65" height="56" viewBox="0 0 65 56" fill="#1c1917">
      <ellipse cx="36" cy="36" rx="24" ry="13" />
      <rect x="14" y="18" width="8" height="20" rx="4" />
      <ellipse cx="13" cy="14" rx="8" ry="6" />
      <path d="M14 9 Q19 7 22 11 Q21 16 17 19" />
      <path d="M26 25 Q28 36 25 48" stroke="white" strokeWidth="1.5" fill="none" opacity="0.6" />
      <path d="M34 24 Q36 35 34 47" stroke="white" strokeWidth="1.5" fill="none" opacity="0.6" />
      <path d="M42 25 Q44 35 43 47" stroke="white" strokeWidth="1.5" fill="none" opacity="0.6" />
      <rect x="14" y="46" width="5" height="10" rx="2" />
      <rect x="24" y="46" width="5" height="10" rx="2" />
      <rect x="38" y="46" width="5" height="10" rx="2" />
      <rect x="50" y="46" width="5" height="10" rx="2" />
    </svg>
  );
}

function SafariWalkers() {
  return (
    <>
      <div className="safari-walker safari-walker--ltr" style={{ bottom: '48px', animationDuration: '42s', animationDelay: '-8s' }}>
        <GiraffeSVG />
      </div>
      <div className="safari-walker safari-walker--rtl" style={{ bottom: '44px', animationDuration: '58s', animationDelay: '-22s' }}>
        <div style={{ transform: 'scaleX(-1)' }}><ElephantSVG /></div>
      </div>
      <div className="safari-walker safari-walker--rtl" style={{ bottom: '46px', animationDuration: '33s', animationDelay: '-5s' }}>
        <div style={{ transform: 'scaleX(-1)' }}><ZebraSVG /></div>
      </div>
    </>
  );
}

function SafariBirds() {
  return (
    <>
      <div className="safari-bird safari-bird--ltr" style={{ top: '8%',  animationDuration: '68s', animationDelay: '-14s' }}>
        <svg width="52" height="18" viewBox="0 0 52 18" fill="none">
          <path d="M0 12 Q13 2 26 9 Q39 2 52 12" stroke="#92400e" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <div className="safari-bird safari-bird--ltr" style={{ top: '12%', animationDuration: '88s', animationDelay: '-40s' }}>
        <svg width="36" height="12" viewBox="0 0 36 12" fill="none">
          <path d="M0 8 Q9 1 18 6 Q27 1 36 8" stroke="#92400e" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <div className="safari-bird safari-bird--rtl" style={{ top: '6%',  animationDuration: '54s', animationDelay: '-22s' }}>
        <svg width="28" height="10" viewBox="0 0 28 10" fill="none">
          <path d="M0 7 Q7 1 14 5 Q21 1 28 7" stroke="#92400e" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    </>
  );
}

// ── Rainforest ───────────────────────────────────────────────────────────────

function RainforestRain({ seed }: { seed: number }) {
  const drops = useMemo(() => Array.from({ length: 55 }, (_, i) => ({
    id: i,
    left: seededRandom(seed + i * 7) * 100,
    duration: 0.55 + seededRandom(seed + i * 11) * 0.55,
    delay: -(seededRandom(seed + i * 13) * 2.5),
    opacity: 0.18 + seededRandom(seed + i * 17) * 0.30,
    height: 10 + seededRandom(seed + i * 19) * 14,
  })), [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {drops.map(d => (
        <div key={d.id} className="rainforest-raindrop" style={{
          left: `${d.left}%`, height: `${d.height}px`, opacity: d.opacity,
          '--rain-duration': `${d.duration}s`, '--rain-delay': `${d.delay}s`,
        } as React.CSSProperties} />
      ))}
    </div>
  );
}

function RainforestFireflies({ seed }: { seed: number }) {
  const colors = ['#fde047', '#a3e635', '#34d399', '#fbbf24', '#6ee7b7', '#bef264'];
  const flies = useMemo(() => Array.from({ length: 7 }, (_, i) => ({
    id: i,
    left: 5 + seededRandom(seed + i * 31) * 88,
    top: 15 + seededRandom(seed + i * 37) * 58,
    size: 5 + seededRandom(seed + i * 41) * 4,
    driftNum: (i % 4) + 1,
    driftDuration: 6 + seededRandom(seed + i * 43) * 9,
    driftDelay: -(seededRandom(seed + i * 47) * 12),
    color: colors[i % colors.length],
  })), [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[2] overflow-hidden">
      {flies.map(f => (
        <div key={f.id}
          className={`theme-firefly theme-firefly--${f.driftNum}`}
          style={{
            left: `${f.left}%`, top: `${f.top}%`,
            width: `${f.size}px`, height: `${f.size}px`,
            '--firefly-color': f.color,
            '--firefly-duration': `${f.driftDuration}s`,
            '--firefly-delay': `${f.driftDelay}s`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

function RainforestVines() {
  const vines = [
    { x: 18,   dur: 5,   delay: 0   },
    { x: 52,   dur: 6.5, delay: -2  },
    { x: 84,   dur: 4.5, delay: -1  },
    { x: 1356, dur: 5.5, delay: -3  },
    { x: 1388, dur: 6,   delay: -1.5},
    { x: 1418, dur: 4,   delay: -0.5},
  ];
  return (
    <div className="theme-rainforest-vines">
      {vines.map((v, i) => (
        <div key={i} className="single-vine"
          style={{ left: `${v.x}px`, '--vine-duration': `${v.dur}s`, '--vine-delay': `${v.delay}s` } as React.CSSProperties}>
          <svg width="32" height="210" viewBox="0 0 32 210">
            <path d="M16 0 Q8 32 17 65 Q9 98 18 130 Q10 162 19 192 Q13 202 16 210"
              stroke="#15803d" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            <ellipse cx="10" cy="58"  rx="15" ry="5.5" fill="#166534" fillOpacity="0.72" transform="rotate(-30 10 58)"  />
            <ellipse cx="19" cy="110" rx="14" ry="5"   fill="#15803d" fillOpacity="0.65" transform="rotate(26 19 110)"  />
            <ellipse cx="11" cy="162" rx="13" ry="4.5" fill="#14532d" fillOpacity="0.60" transform="rotate(-22 11 162)" />
          </svg>
        </div>
      ))}
    </div>
  );
}

// ── Enchanted Castle ─────────────────────────────────────────────────────────

function CastleWisps({ seed }: { seed: number }) {
  const wispColors = ['rgba(196,181,253,0.8)', 'rgba(252,211,77,0.75)', 'rgba(52,211,153,0.65)', 'rgba(251,146,60,0.70)'];
  const wisps = useMemo(() => Array.from({ length: 4 }, (_, i) => ({
    id: i,
    left: 6 + seededRandom(seed + i * 53) * 82,
    top: 12 + seededRandom(seed + i * 59) * 52,
    size: 14 + seededRandom(seed + i * 61) * 10,
    driftNum: (i % 4) + 1,
    duration: 9 + seededRandom(seed + i * 67) * 8,
    delay: -(seededRandom(seed + i * 71) * 14),
    color: wispColors[i % wispColors.length],
  })), [seed]);

  const stars = useMemo(() => Array.from({ length: 3 }, (_, i) => ({
    id: i,
    top: 4 + seededRandom(seed + i * 77) * 28,
    left: 8 + seededRandom(seed + i * 83) * 55,
    loopDuration: 12 + seededRandom(seed + i * 89) * 14,
    delay: -(seededRandom(seed + i * 97) * 20),
  })), [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[2] overflow-hidden">
      {wisps.map(w => (
        <div key={w.id} className={`castle-wisp castle-wisp--${w.driftNum}`} style={{
          left: `${w.left}%`, top: `${w.top}%`,
          width: `${w.size}px`, height: `${w.size}px`,
          '--wisp-color': w.color,
          '--wisp-duration': `${w.duration}s`,
          '--wisp-delay': `${w.delay}s`,
        } as React.CSSProperties} />
      ))}
      {stars.map(s => (
        <div key={s.id} className="castle-shooting-star" style={{
          top: `${s.top}%`, left: `${s.left}%`,
          '--star-loop': `${s.loopDuration}s`,
          '--star-delay': `${s.delay}s`,
        } as React.CSSProperties} />
      ))}
    </div>
  );
}

function CastleBats() {
  return (
    <>
      <div className="castle-bat" style={{ top: '18%', animationDuration: '22s', animationDelay: '-4s' }}>
        <svg width="32" height="16" viewBox="0 0 32 16" fill="#1e0a3c">
          <path d="M16 8 Q10 0 0 5 Q5 8 8 8 Q11 12 16 8 Q21 12 24 8 Q27 8 32 5 Q22 0 16 8 Z" />
        </svg>
      </div>
      <div className="castle-bat castle-bat--rtl" style={{ top: '23%', animationDuration: '30s', animationDelay: '-12s' }}>
        <svg width="22" height="11" viewBox="0 0 22 11" fill="#2d1060">
          <path d="M11 5.5 Q7 0 0 3.5 Q4 6 6 6 Q8 8.5 11 5.5 Q14 8.5 16 6 Q18 6 22 3.5 Q15 0 11 5.5 Z" />
        </svg>
      </div>
      <div className="castle-bat" style={{ top: '14%', animationDuration: '17s', animationDelay: '-8s' }}>
        <svg width="18" height="9" viewBox="0 0 18 9" fill="#3b0764" fillOpacity="0.85">
          <path d="M9 4.5 Q6 0 0 3 Q3 5 5 5 Q6.5 7 9 4.5 Q11.5 7 13 5 Q15 5 18 3 Q12 0 9 4.5 Z" />
        </svg>
      </div>
    </>
  );
}

// ── Arctic ───────────────────────────────────────────────────────────────────

function ArcticSnow({ seed }: { seed: number }) {
  const flakes = useMemo(() => Array.from({ length: 48 }, (_, i) => ({
    id: i,
    left: seededRandom(seed + i * 7) * 100,
    size: 3 + seededRandom(seed + i * 11) * 9,
    duration: 7 + seededRandom(seed + i * 13) * 15,
    delay: -(seededRandom(seed + i * 17) * 22),
    driftX: -30 + seededRandom(seed + i * 23) * 60,
    opacity: 0.35 + seededRandom(seed + i * 29) * 0.55,
  })), [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {flakes.map(f => (
        <div key={f.id} className="arctic-snowflake" style={{
          left: `${f.left}%`, width: `${f.size}px`, height: `${f.size}px`,
          '--snow-duration': `${f.duration}s`, '--snow-delay': `${f.delay}s`,
          '--snow-drift': `${f.driftX}px`, opacity: f.opacity,
        } as React.CSSProperties} />
      ))}
    </div>
  );
}

function ArcticScene() {
  return (
    <div className="theme-arctic-scene">
      <svg viewBox="0 0 1440 260" preserveAspectRatio="none" width="100%" height="100%">
        <defs>
          <linearGradient id="iceberg-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.82" />
            <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.45" />
          </linearGradient>
          <linearGradient id="iceberg-grad-2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f0f9ff" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.35" />
          </linearGradient>
        </defs>
        {/* Large icebergs left */}
        <path d="M-20 260 L18 165 L58 140 L96 162 L128 260 Z" fill="url(#iceberg-grad)" />
        <path d="M72 260 L96 195 L124 178 L154 198 L178 260 Z" fill="url(#iceberg-grad-2)" />
        {/* Large icebergs right */}
        <path d="M1460 260 L1422 165 L1382 140 L1344 162 L1312 260 Z" fill="url(#iceberg-grad)" />
        <path d="M1368 260 L1344 195 L1316 178 L1286 198 L1262 260 Z" fill="url(#iceberg-grad-2)" />
        {/* Snow ground rolls */}
        <path d="M0 232 Q80 212 180 224 Q280 236 400 218 Q520 200 640 222 Q760 244 880 226 Q1000 208 1120 222 Q1240 236 1360 218 Q1420 210 1440 214 L1440 260 L0 260 Z"
          fill="#e0f2fe" fillOpacity="0.42" />
        <path d="M0 248 Q200 232 440 242 Q680 252 720 246 Q760 240 1000 248 Q1240 256 1440 244 L1440 260 L0 260 Z"
          fill="white" fillOpacity="0.30" />
      </svg>
    </div>
  );
}

// ── Pirate ───────────────────────────────────────────────────────────────────

function PirateScene() {
  return (
    <div className="theme-pirate-scene">
      <svg viewBox="0 0 1440 420" preserveAspectRatio="none" width="100%" height="100%">
        <defs>
          <linearGradient id="pirate-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#060d10" stopOpacity="0" />
            <stop offset="55%"  stopColor="#0a1a1f" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#061520" stopOpacity="0.80" />
          </linearGradient>
          {/* Deep ocean water */}
          <linearGradient id="pirate-water" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#0a3040" stopOpacity="0.70" />
            <stop offset="100%" stopColor="#040e18" stopOpacity="0.98" />
          </linearGradient>
          {/* Moon glow */}
          <radialGradient id="pirate-moon-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#fffde7" stopOpacity="0.95" />
            <stop offset="40%"  stopColor="#fef3c7" stopOpacity="0.55" />
            <stop offset="75%"  stopColor="#fde68a" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#fde68a" stopOpacity="0" />
          </radialGradient>
          {/* Moon path shimmer on water */}
          <linearGradient id="pirate-moon-path" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#fef9c3" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#fef9c3" stopOpacity="0.04" />
          </linearGradient>
          {/* Ship hull — warm dark brown wood */}
          <linearGradient id="pirate-hull" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#5c2d0a" stopOpacity="1" />
            <stop offset="55%"  stopColor="#3d1c06" stopOpacity="1" />
            <stop offset="100%" stopColor="#1c0802" stopOpacity="1" />
          </linearGradient>
          {/* Stern cabin face */}
          <linearGradient id="pirate-stern" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#6b3510" stopOpacity="1" />
            <stop offset="100%" stopColor="#3d1c06" stopOpacity="1" />
          </linearGradient>
          {/* Sail canvas — off-white aged linen */}
          <linearGradient id="pirate-sail-main" x1="0" y1="0" x2="0.3" y2="1">
            <stop offset="0%"   stopColor="#f5f0e0" stopOpacity="0.95" />
            <stop offset="50%"  stopColor="#e8dfc0" stopOpacity="0.90" />
            <stop offset="100%" stopColor="#d4c89a" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="pirate-sail-fore" x1="0" y1="0" x2="0.2" y2="1">
            <stop offset="0%"   stopColor="#ede5cc" stopOpacity="0.90" />
            <stop offset="100%" stopColor="#cfc095" stopOpacity="0.82" />
          </linearGradient>
          <linearGradient id="pirate-sail-top" x1="0" y1="0" x2="0.2" y2="1">
            <stop offset="0%"   stopColor="#f0ead6" stopOpacity="0.88" />
            <stop offset="100%" stopColor="#d9cfa8" stopOpacity="0.80" />
          </linearGradient>
        </defs>

        {/* Sky wash */}
        <rect x="0" y="0" width="1440" height="420" fill="url(#pirate-sky)" />

        {/* Stars — scattered dots */}
        {[
          [130,18],[290,8],[440,22],[600,12],[750,6],[920,20],[1080,10],[1240,16],[1380,8],
          [60,38],[360,30],[520,42],[680,28],[840,38],[1000,32],[1160,44],[1320,26],
          [180,55],[400,60],[620,50],[810,58],[1050,52],[1280,56],
        ].map(([cx,cy],i) => (
          <circle key={i} cx={cx} cy={cy} r={i%3===0?1.2:0.8} fill="#fef9c3" fillOpacity={0.35+0.3*(i%3)} />
        ))}

        {/* Moon with big atmospheric halo */}
        <circle cx="240" cy="72" r="80"  fill="url(#pirate-moon-halo)" />
        <circle cx="240" cy="72" r="32"  fill="#fffde7" fillOpacity="0.88" />
        {/* subtle crater marks */}
        <circle cx="228" cy="64" r="5"  fill="#fde68a" fillOpacity="0.35" />
        <circle cx="248" cy="78" r="3.5" fill="#fde68a" fillOpacity="0.28" />

        {/* Moon path shimmering on water */}
        <path d="M80 338 Q120 328 160 336 Q200 344 240 336 Q200 330 160 324 Q120 318 80 324 Z"
          fill="url(#pirate-moon-path)" className="pirate-moon-path" />

        {/* Distant island silhouette — left (in unshielded left zone ~0-22%) */}
        <path d="M0 305 Q55 278 128 283 Q185 280 228 294 Q245 300 228 308 L0 324 Z"
          fill="#071a12" fillOpacity="0.88" />
        {/* Palm 1 on island */}
        <path d="M108 283 Q105 258 100 238 Q97 222 104 206"
          stroke="#2a1200" strokeWidth="5" fill="none" strokeLinecap="round" />
        <path d="M104 206 Q80 188 62 185"  stroke="#14532d" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M104 206 Q86 180 92 164"  stroke="#14532d" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M104 206 Q110 174 124 164" stroke="#14532d" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M104 206 Q124 190 144 192" stroke="#14532d" strokeWidth="3" fill="none" strokeLinecap="round" strokeOpacity="0.75" />
        {/* Palm 2 shorter */}
        <path d="M148 287 Q146 268 142 254 Q140 244 146 234"
          stroke="#2a1200" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M146 234 Q130 222 118 222" stroke="#14532d" strokeWidth="3" fill="none" strokeLinecap="round" strokeOpacity="0.85" />
        <path d="M146 234 Q152 220 164 218" stroke="#14532d" strokeWidth="3" fill="none" strokeLinecap="round" strokeOpacity="0.85" />
        <path d="M146 234 Q158 228 170 232" stroke="#14532d" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeOpacity="0.70" />

        {/* Treasure chest on island shore */}
        <rect x="44" y="298" width="32" height="22" rx="3" fill="#7c2d12" fillOpacity="0.92" />
        <rect x="44" y="298" width="32" height="9"  rx="3" fill="#92400e" fillOpacity="0.90" />
        <rect x="55" y="304" width="10" height="7"  rx="1.5" fill="#b45309" fillOpacity="0.95" />
        <circle cx="50" cy="302" r="2.2" fill="#fbbf24" fillOpacity="1"   className="pirate-glint" />
        <circle cx="70" cy="301" r="1.6" fill="#fbbf24" fillOpacity="0.90" className="pirate-glint" />

        {/* ── OCEAN ── */}
        <rect x="0" y="328" width="1440" height="92" fill="url(#pirate-water)" />

        {/* Wave crests — 3 layers */}
        <path d="M0 328 Q72 318 144 328 Q216 338 288 328 Q360 318 432 328 Q504 338 576 328 Q648 318 720 328 Q792 338 864 328 Q936 318 1008 328 Q1080 338 1152 328 Q1224 318 1296 328 Q1368 338 1440 328"
          stroke="#1a6b5a" strokeWidth="3" fill="none" strokeOpacity="0.70" className="pirate-wave-svg pirate-wave-svg--1" />
        <path d="M0 342 Q80 332 160 342 Q240 352 320 342 Q400 332 480 342 Q560 352 640 342 Q720 332 800 342 Q880 352 960 342 Q1040 332 1120 342 Q1200 352 1280 342 Q1360 332 1440 342"
          stroke="#0f5244" strokeWidth="2.5" fill="none" strokeOpacity="0.55" className="pirate-wave-svg pirate-wave-svg--2" />
        <path d="M0 356 Q90 348 180 356 Q270 364 360 356 Q450 348 540 356 Q630 364 720 356 Q810 348 900 356 Q990 364 1080 356 Q1170 348 1260 356 Q1350 364 1440 356"
          stroke="#0a3d30" strokeWidth="2" fill="none" strokeOpacity="0.40" className="pirate-wave-svg pirate-wave-svg--3" />
        {/* White foam caps */}
        {[72,216,360,504,648,792,936,1080,1224,1368].map((cx,i) => (
          <ellipse key={i} cx={cx} cy={328} rx={14} ry={2.5} fill="#e0f2f1" fillOpacity={0.22} />
        ))}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* PIRATE SHIP — shifted right into the unshielded zone     */}
        {/* ══════════════════════════════════════════════════════════ */}
        <g transform="translate(380 -40)">

        {/* ══════════════════════════════════════════════════════════ */}
        {/* 3-DECK GALLEON — broad hull, tall stern castle, big bow   */}
        {/* All coords in ship-local space; ocean SVG y≈328           */}
        {/* ══════════════════════════════════════════════════════════ */}

        {/* ── BOWSPRIT (behind everything) ── */}
        <line x1="598" y1="242" x2="488" y2="192" stroke="#7c3a0a" strokeWidth="9" strokeLinecap="round" />
        <line x1="598" y1="242" x2="490" y2="194" stroke="#5c2a06" strokeWidth="4"  strokeLinecap="round" />
        {/* Jib sail 1 */}
        <path d="M490 194 Q516 210 562 228 L552 242 Z" fill="url(#pirate-sail-fore)" fillOpacity="0.88" />
        {/* Jib sail 2 (inner) */}
        <path d="M518 204 Q538 218 570 230 L564 242 Z" fill="url(#pirate-sail-top)" fillOpacity="0.80" />
        {/* Bowsprit top spar */}
        <line x1="490" y1="194" x2="508" y2="188" stroke="#7c3a0a" strokeWidth="4" strokeLinecap="round" />

        {/* ── MAIN HULL BODY — 3-deck galleon ── */}
        {/*
          Hull runs x=560 (bow) to x=1120 (stern).
          Top deck rail: ~y=208
          Deck 2 wale: ~y=248
          Deck 3 wale: ~y=284
          Keel/waterline: ~y=340
          Bow tip projects to ~y=225 with a tall forecastle up to ~y=188
          Stern castle rises to ~y=155
        */}

        {/* Hull bottom / keel — full dark underbelly */}
        <path d="M572 310 Q640 330 800 338 Q960 344 1080 338 Q1108 334 1112 322 L1112 340 Q1080 360 800 362 Q560 358 540 340 Q534 328 540 318 Z"
          fill="#1c0802" fillOpacity="0.95" />

        {/* Main hull side — three decks with tumblehome */}
        <path d="
          M 560 225
          Q 574 208 614 200
          L 830 196
          Q 960 193 1028 198
          Q 1068 202 1082 212
          L 1112 240
          L 1112 340
          Q 1080 352 800 356
          Q 560 352 538 336
          L 540 318
          Q 548 298 562 285
          Q 556 270 556 252
          Q 554 236 560 225
          Z"
          fill="url(#pirate-hull)" />

        {/* ── DECK WALE LINES (3 decks) ── */}
        {/* Top deck rail */}
        <path d="M 614 200 Q 830 196 1028 198 Q 1068 202 1082 212"
          stroke="#8b4a18" strokeWidth="3.5" fill="none" strokeOpacity="0.90" />
        {/* Gold stripe — top deck */}
        <path d="M 616 204 Q 830 200 1026 202 Q 1066 206 1080 216"
          stroke="#d97706" strokeWidth="2.5" fill="none" strokeOpacity="0.75" />

        {/* Deck 2 wale */}
        <path d="M 558 246 Q 600 238 830 234 Q 1010 232 1090 242"
          stroke="#8b4a18" strokeWidth="3" fill="none" strokeOpacity="0.80" />
        {/* Gold stripe — deck 2 */}
        <path d="M 558 250 Q 600 242 830 238 Q 1010 236 1090 246"
          stroke="#d97706" strokeWidth="2" fill="none" strokeOpacity="0.65" />

        {/* Deck 3 wale */}
        <path d="M 558 282 Q 600 276 830 272 Q 1010 270 1100 280"
          stroke="#8b4a18" strokeWidth="2.5" fill="none" strokeOpacity="0.70" />
        {/* Gold stripe — deck 3 */}
        <path d="M 558 286 Q 600 280 830 276 Q 1010 274 1100 284"
          stroke="#d97706" strokeWidth="1.8" fill="none" strokeOpacity="0.55" />

        {/* Hull plank texture lines */}
        <path d="M 564 306 Q 830 300 1104 308" stroke="#2a0e04" strokeWidth="1.2" fill="none" strokeOpacity="0.50" />
        <path d="M 566 318 Q 830 313 1106 320" stroke="#2a0e04" strokeWidth="1.0" fill="none" strokeOpacity="0.40" />

        {/* ── GUN PORTS — two rows ── */}
        {/* Deck 2 gun ports */}
        {[638,682,726,770,814,858,902,946,990,1034].map((x,i) => (
          <g key={`d2-${i}`}>
            <rect x={x} y={220} width={16} height={11} rx={2} fill="#120500" fillOpacity={0.95} />
            <rect x={x+2} y={222} width={12} height={7}  rx={1} fill="#060200" fillOpacity={1.00} />
          </g>
        ))}
        {/* Deck 3 gun ports */}
        {[618,662,706,750,794,838,882,926,970,1014,1058].map((x,i) => (
          <g key={`d3-${i}`}>
            <rect x={x} y={258} width={14} height={10} rx={2} fill="#120500" fillOpacity={0.90} />
            <rect x={x+2} y={260} width={10} height={6}  rx={1} fill="#060200" fillOpacity={0.95} />
          </g>
        ))}

        {/* ── BOW / FORECASTLE ── */}
        {/* Forecastle deck extending over the bow */}
        <path d="M 560 225 Q 548 212 550 196 Q 554 182 570 178 L 640 178 L 614 200 Q 590 210 570 218 Z"
          fill="#5c2a06" />
        <path d="M 570 178 L 640 178 L 636 182 L 572 182 Z" fill="#7c3a0a" fillOpacity="0.90" />
        {/* Bow figurehead spar */}
        <line x1="554" y1="196" x2="528" y2="176" stroke="#7c3a0a" strokeWidth="5" strokeLinecap="round" />
        {/* Bow railing */}
        <line x1="570" y1="178" x2="640" y2="178" stroke="#a05a20" strokeWidth="3" />
        {[576,592,608,624,640].map((x,i) => (
          <line key={i} x1={x} y1={175} x2={x} y2={180} stroke="#a05a20" strokeWidth="2.5" />
        ))}
        {/* Top rail */}
        <line x1="614" y1="200" x2="640" y2="178" stroke="#a05a20" strokeWidth="2" strokeOpacity="0.75" />

        {/* ── STERN CASTLE — tall 3-level rear ── */}
        {/* Stern castle body */}
        <path d="M 1082 212 L 1082 155 L 1120 155 L 1120 212 Q 1115 230 1112 240 Z"
          fill="url(#pirate-stern)" />
        {/* Level 2 of stern */}
        <path d="M 1082 155 L 1082 128 L 1116 128 L 1116 155 Z"
          fill="#6b3510" fillOpacity="0.95" />
        {/* Level 3 top (captain's cabin) */}
        <path d="M 1084 128 L 1084 108 L 1112 108 L 1112 128 Z"
          fill="#5c2a06" fillOpacity="0.95" />
        {/* Stern top rail */}
        <line x1="1082" y1="108" x2="1114" y2="108" stroke="#a05a20" strokeWidth="3" />
        {[1086,1094,1102,1110].map((x,i) => (
          <line key={i} x1={x} y1={105} x2={x} y2={110} stroke="#a05a20" strokeWidth="2.5" />
        ))}

        {/* Stern windows — 3 levels, amber flicker */}
        {/* Level 1 */}
        <rect x="1086" y="216" width="14" height="11" rx="2" fill="#fbbf24" fillOpacity="0.60" className="pirate-lantern pirate-win-a" />
        <rect x="1104" y="216" width="14" height="11" rx="2" fill="#fbbf24" fillOpacity="0.55" className="pirate-lantern pirate-win-b" />
        {/* Level 2 */}
        <rect x="1086" y="162" width="14" height="11" rx="2" fill="#f59e0b" fillOpacity="0.65" className="pirate-lantern pirate-win-c" />
        <rect x="1104" y="162" width="14" height="11" rx="2" fill="#f59e0b" fillOpacity="0.60" className="pirate-lantern pirate-win-a" />
        <rect x="1086" y="178" width="14" height="9"  rx="2" fill="#f59e0b" fillOpacity="0.50" className="pirate-lantern pirate-win-b" />
        <rect x="1104" y="178" width="14" height="9"  rx="2" fill="#f59e0b" fillOpacity="0.45" className="pirate-lantern pirate-win-c" />
        {/* Level 3 */}
        <rect x="1088" y="114" width="12" height="9"  rx="2" fill="#fef08a" fillOpacity="0.70" className="pirate-lantern pirate-win-b" />
        <rect x="1104" y="114" width="10" height="9"  rx="2" fill="#fef08a" fillOpacity="0.65" className="pirate-lantern pirate-win-a" />
        {/* Stern balcony rail level 2 */}
        <line x1="1082" y1="155" x2="1120" y2="155" stroke="#a05a20" strokeWidth="2.5" />
        {[1086,1094,1102,1110,1118].map((x,i) => (
          <line key={i} x1={x} y1={153} x2={x} y2={157} stroke="#a05a20" strokeWidth="2" />
        ))}
        {/* Stern balcony rail level 3 */}
        <line x1="1082" y1="128" x2="1116" y2="128" stroke="#a05a20" strokeWidth="2" />

        {/* ── MAIN MAST (tallest) ── */}
        <line x1="820" y1="200" x2="820" y2="50"  stroke="#7c3a0a" strokeWidth="9" strokeLinecap="round" />
        <line x1="820" y1="200" x2="820" y2="50"  stroke="#5c2a06" strokeWidth="4" strokeLinecap="round" />
        {/* Lower yard */}
        <line x1="740" y1="100" x2="900" y2="100" stroke="#7c3a0a" strokeWidth="6" strokeLinecap="round" />
        {/* Top yard */}
        <line x1="770" y1="70"  x2="870" y2="70"  stroke="#6b3510" strokeWidth="4.5" strokeLinecap="round" />
        {/* Crow's nest */}
        <path d="M798 50 L798 38 L842 38 L842 50 Z" fill="#4a1e08" />
        <line x1="794" y1="38" x2="846" y2="38"  stroke="#7c3a0a" strokeWidth="3" />
        {[800,812,820,830,842].map((x,i) => (
          <line key={i} x1={x} y1={37} x2={x} y2={50} stroke="#7c3a0a" strokeWidth="1.5" />
        ))}

        {/* Main lower sail — large billowed */}
        <path d="M740 100 Q748 128 754 174 Q754 192 820 196 Q886 192 886 174 Q892 128 900 100 Z"
          fill="url(#pirate-sail-main)" />
        <line x1="774" y1="100" x2="780" y2="194" stroke="#c8b87a" strokeWidth="1.5" strokeOpacity="0.60" />
        <line x1="820" y1="100" x2="820" y2="196" stroke="#c8b87a" strokeWidth="1.5" strokeOpacity="0.55" />
        <line x1="866" y1="100" x2="860" y2="194" stroke="#c8b87a" strokeWidth="1.5" strokeOpacity="0.55" />
        <line x1="746" y1="132" x2="894" y2="132" stroke="#c8b87a" strokeWidth="1.2" strokeOpacity="0.45" />
        <line x1="748" y1="160" x2="892" y2="160" stroke="#c8b87a" strokeWidth="1.0" strokeOpacity="0.40" />

        {/* Skull & crossbones — large on main sail */}
        <circle cx="820" cy="148" r="17"  fill="#1a0a02" fillOpacity="0.88" />
        <circle cx="820" cy="144" r="9"   fill="#f5f0e0" fillOpacity="0.95" />
        <circle cx="815" cy="142" r="2.5" fill="#1a0a02" />
        <circle cx="825" cy="142" r="2.5" fill="#1a0a02" />
        <path d="M814 147 Q820 151 826 147" stroke="#1a0a02" strokeWidth="2" fill="none" />
        <line x1="810" y1="153" x2="830" y2="165" stroke="#f5f0e0" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.90" />
        <line x1="830" y1="153" x2="810" y2="165" stroke="#f5f0e0" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.90" />

        {/* Main topsail */}
        <path d="M770 70 Q775 80 784 96 Q820 100 856 96 Q865 80 870 70 Z"
          fill="url(#pirate-sail-top)" />
        <line x1="820" y1="70" x2="820" y2="100" stroke="#c8b87a" strokeWidth="1.2" strokeOpacity="0.50" />

        {/* ── FOREMAST ── */}
        <line x1="940" y1="200" x2="940" y2="82"  stroke="#7c3a0a" strokeWidth="8" strokeLinecap="round" />
        <line x1="940" y1="200" x2="940" y2="82"  stroke="#5c2a06" strokeWidth="3.5" strokeLinecap="round" />
        <line x1="876" y1="116" x2="1004" y2="116" stroke="#7c3a0a" strokeWidth="5.5" strokeLinecap="round" />
        <line x1="900" y1="92"  x2="980"  y2="92"  stroke="#6b3510" strokeWidth="4" strokeLinecap="round" />
        {/* Fore lower sail */}
        <path d="M876 116 Q884 140 888 172 Q940 176 992 172 Q996 140 1004 116 Z"
          fill="url(#pirate-sail-fore)" />
        <line x1="908" y1="116" x2="912" y2="174" stroke="#c8b87a" strokeWidth="1.2" strokeOpacity="0.55" />
        <line x1="940" y1="116" x2="940" y2="176" stroke="#c8b87a" strokeWidth="1.2" strokeOpacity="0.55" />
        <line x1="972" y1="116" x2="968" y2="174" stroke="#c8b87a" strokeWidth="1.2" strokeOpacity="0.55" />
        {/* Fore topsail */}
        <path d="M900 92 Q906 100 916 112 Q940 116 964 112 Q974 100 980 92 Z"
          fill="url(#pirate-sail-top)" />

        {/* ── MIZZEN MAST ── */}
        <line x1="1058" y1="212" x2="1058" y2="130" stroke="#7c3a0a" strokeWidth="6" strokeLinecap="round" />
        <line x1="1058" y1="212" x2="1058" y2="130" stroke="#5c2a06" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="1018" y1="148" x2="1098" y2="148" stroke="#7c3a0a" strokeWidth="4.5" strokeLinecap="round" />
        {/* Mizzen sail */}
        <path d="M1018 148 Q1024 164 1028 192 Q1058 198 1088 192 Q1092 164 1098 148 Z"
          fill="url(#pirate-sail-fore)" fillOpacity="0.85" />
        <line x1="1058" y1="148" x2="1058" y2="198" stroke="#c8b87a" strokeWidth="1.2" strokeOpacity="0.50" />

        {/* ── RIGGING ── */}
        <line x1="820" y1="50"  x2="1120" y2="210" stroke="#8b6914" strokeWidth="2"   strokeOpacity="0.70" />
        <line x1="820" y1="50"  x2="598"  y2="238" stroke="#8b6914" strokeWidth="2"   strokeOpacity="0.65" />
        <line x1="820" y1="50"  x2="940"  y2="82"  stroke="#8b6914" strokeWidth="1.5" strokeOpacity="0.60" />
        <line x1="940" y1="82"  x2="1058" y2="130" stroke="#8b6914" strokeWidth="1.5" strokeOpacity="0.55" />
        <line x1="1058" y1="130" x2="1120" y2="160" stroke="#8b6914" strokeWidth="1.2" strokeOpacity="0.50" />
        <line x1="488"  y1="192" x2="820"  y2="100" stroke="#8b6914" strokeWidth="1.2" strokeOpacity="0.50" />
        <line x1="488"  y1="192" x2="598"  y2="230" stroke="#8b6914" strokeWidth="1.0" strokeOpacity="0.45" />
        <line x1="740"  y1="100" x2="876"  y2="116" stroke="#8b6914" strokeWidth="1.0" strokeOpacity="0.42" />
        <line x1="900"  y1="100" x2="1004" y2="116" stroke="#8b6914" strokeWidth="1.0" strokeOpacity="0.42" />
        {/* Ratlines (horizontal rope ladders on shrouds) */}
        {[80,100,120,140,160].map((y,i) => (
          <line key={i} x1={820+(y-50)*0.55} y1={y} x2={820+(y-50)*(-0.55)+820*0} y2={y}
            stroke="#8b6914" strokeWidth="0.7" strokeOpacity="0.30" />
        ))}

        {/* ── JOLLY ROGER FLAG ── */}
        <line x1="820" y1="50" x2="820" y2="24" stroke="#7c3a0a" strokeWidth="4" />
        <rect x="820" y="24" width="52" height="34" rx="2" fill="#0a0a0a" fillOpacity="0.97" />
        <circle cx="838" cy="37"  r="8.5" fill="#f5f0e0" fillOpacity="0.95" />
        <circle cx="834" cy="35"  r="2.5" fill="#0a0a0a" />
        <circle cx="842" cy="35"  r="2.5" fill="#0a0a0a" />
        <path d="M833 40 Q838 44 843 40" stroke="#0a0a0a" strokeWidth="2" fill="none" />
        <line x1="828" y1="45" x2="848" y2="57" stroke="#f5f0e0" strokeWidth="3"   strokeLinecap="round" strokeOpacity="0.90" />
        <line x1="848" y1="45" x2="828" y2="57" stroke="#f5f0e0" strokeWidth="3"   strokeLinecap="round" strokeOpacity="0.90" />
        {/* Flag flutter */}
        <path d="M872 24 L872 58 L896 41 Z" fill="#0a0a0a" fillOpacity="0.90" />

        {/* ── STERN LANTERN ── */}
        <line x1="1120" y1="106" x2="1120" y2="120" stroke="#7c3a0a" strokeWidth="3" />
        <rect x="1114" y="120" width="12" height="14" rx="3" fill="#4a1e08" />
        <circle cx="1120" cy="127" r="5.5" fill="#fbbf24" fillOpacity="0.85" className="pirate-lantern" />
        <circle cx="1120" cy="127" r="14"  fill="#f59e0b" fillOpacity="0.22" className="pirate-lantern" />
        <ellipse cx="1120" cy="358" rx="24" ry="5" fill="#d97706" fillOpacity="0.22" className="pirate-lantern" />
        <ellipse cx="1120" cy="368" rx="38" ry="5" fill="#d97706" fillOpacity="0.12" className="pirate-lantern" />

        </g>{/* end ship group */}
      </svg>
    </div>
  );
}

function PirateWaves({ seed }: { seed: number }) {
  const waves = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    id: i,
    delay: -(seededRandom(seed + i * 17) * 5),
    duration: 3.5 + seededRandom(seed + i * 23) * 2.5,
    offset: seededRandom(seed + i * 31) * 8,
  })), [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {waves.map(w => (
        <div key={w.id} className="pirate-wave" style={{
          '--wave-duration': `${w.duration}s`,
          '--wave-delay': `${w.delay}s`,
          '--wave-offset': `${w.offset}px`,
        } as React.CSSProperties} />
      ))}
    </div>
  );
}

// ── Candy ────────────────────────────────────────────────────────────────────

function CandyBubbles({ seed }: { seed: number }) {
  const bubbleColors = [
    'rgba(244,114,182,0.45)', 'rgba(192,132,252,0.45)',
    'rgba(96,165,250,0.40)',  'rgba(52,211,153,0.40)',  'rgba(251,191,36,0.45)',
  ];
  const bubbles = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    id: i,
    left: seededRandom(seed + i * 31) * 100,
    bottom: seededRandom(seed + i * 37) * 25,
    size: 10 + seededRandom(seed + i * 41) * 22,
    duration: 2.5 + seededRandom(seed + i * 43) * 4,
    delay: -(seededRandom(seed + i * 47) * 9),
    color: bubbleColors[i % bubbleColors.length],
  })), [seed]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {bubbles.map(b => (
        <div key={b.id} className="candy-bubble" style={{
          left: `${b.left}%`, bottom: `${b.bottom}%`,
          width: `${b.size}px`, height: `${b.size}px`,
          '--bubble-color': b.color,
          '--bubble-duration': `${b.duration}s`,
          '--bubble-delay': `${b.delay}s`,
        } as React.CSSProperties} />
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function ThemeParticles({ config, themeId }: Props) {
  const particles = useMemo(() => {
    const { emojis, density, speed } = config;
    const { min, max } = SPEED_DURATIONS[speed];
    const result: Particle[] = [];

    const layerMods = [
      { scaleMult: 0.60, durationMult: 1.50, opacityMult: 0.65 },
      { scaleMult: 1.00, durationMult: 1.00, opacityMult: 1.00 },
      { scaleMult: 1.45, durationMult: 0.68, opacityMult: 1.15 },
    ];

    for (let i = 0; i < density; i++) {
      const r1 = seededRandom(i * 7  + 1);
      const r2 = seededRandom(i * 13 + 2);
      const r3 = seededRandom(i * 19 + 3);
      const r4 = seededRandom(i * 23 + 4);
      const r5 = seededRandom(i * 31 + 5);
      const r6 = seededRandom(i * 37 + 6);
      const r7 = seededRandom(i * 41 + 7);
      const layer = i % 3;
      const lm = layerMods[layer];
      const emoji = emojis[Math.floor(r1 * emojis.length)];
      const emojiScale = config.emojiScales?.[emoji] ?? 1.0;
      result.push({
        id: i,
        emoji,
        left: r2 < 0.5 ? r2 * 36 : 82 + (r2 - 0.5) * 36,
        duration: (min + r3 * (max - min)) * lm.durationMult,
        delay: -(r4 * max),
        scale: (0.7 + r5 * 0.8) * lm.scaleMult * emojiScale,
        driftX: -40 + r6 * 80,
        driftRotate: -180 + r7 * 360,
        opacity: Math.min(0.75, (0.3 + r5 * 0.35) * lm.opacityMult),
        layer,
      });
    }
    return result;
  }, [config, themeId]);

  const themeSeed = themeId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden" aria-hidden="true">

      {/* ── Dino ── */}
      {themeId === 'theme-dinosaur' && <DinoScene />}

      {/* ── Ocean ── */}
      {themeId === 'theme-ocean' && <OceanScene />}

      {/* ── Space Station ── */}
      {themeId === 'theme-space-station' && <>
        <SpaceStarfield seed={themeSeed} />
        <SpaceScene />
      </>}

      {/* ── Safari ── */}
      {themeId === 'theme-safari' && <>
        <div className="theme-safari-sky-glow" />
        <div className="theme-safari-shimmer" />
        <div className="theme-safari-horizon">
          <svg viewBox="0 0 1440 220" preserveAspectRatio="none" width="100%" height="100%">
            <defs>
              <linearGradient id="safari-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fed7aa" stopOpacity="0" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.28" />
              </linearGradient>
              <radialGradient id="safari-sun" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#f59e0b" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="1440" height="220" fill="url(#safari-sky)" />

            {/* Setting sun */}
            <circle cx="860" cy="195" r="75" fill="url(#safari-sun)" />
            <circle cx="860" cy="195" r="46" fill="#fbbf24" fillOpacity="0.28" />

            {/* Far hills (layer 1 — most distant, dusty) */}
            <path d="M0 168 Q160 122 320 156 Q480 190 640 138 Q800 86  960 144 Q1120 202 1280 148 Q1400 108 1440 132 L1440 220 L0 220 Z"
              fill="#d97706" fillOpacity="0.14" />
            {/* Mid hills (layer 2) */}
            <path d="M0 182 Q200 148 400 172 Q600 196 800 158 Q1000 120 1200 168 Q1360 204 1440 182 L1440 220 L0 220 Z"
              fill="#b45309" fillOpacity="0.20" />

            {/* Grass tufts */}
            <path className="safari-grass" d="M50 185 Q52 175 54 185 M58 183 Q60 172 62 183 M66 185 Q68 176 70 185" stroke="#a16207" strokeWidth="1.5" fill="none" />
            <path className="safari-grass" d="M400 182 Q402 172 404 182 M408 180 Q410 170 412 180" stroke="#a16207" strokeWidth="1.5" fill="none" />
            <path className="safari-grass" d="M900 183 Q902 173 904 183 M908 181 Q910 171 912 181 M916 183 Q918 174 920 183" stroke="#a16207" strokeWidth="1.5" fill="none" />
            <path className="safari-grass" d="M1380 182 Q1382 172 1384 182 M1388 180 Q1390 170 1392 180" stroke="#a16207" strokeWidth="1.5" fill="none" />

            {/* Ground */}
            <path d="M0 190 Q360 176 720 184 Q1080 192 1440 178 L1440 220 L0 220 Z"
              fill="#d97706" fillOpacity="0.35" />

            {/* Acacia left */}
            <rect x="112" y="124" width="8" height="58" fill="#92400e" />
            <ellipse cx="116" cy="118" rx="34" ry="12" fill="#78350f" fillOpacity="0.80" />
            <ellipse cx="94"  cy="112" rx="20" ry="8"  fill="#78350f" fillOpacity="0.64" />
            <ellipse cx="140" cy="113" rx="22" ry="9"  fill="#78350f" fillOpacity="0.64" />
            {/* Acacia right */}
            <rect x="1314" y="114" width="9" height="68" fill="#92400e" />
            <ellipse cx="1318" cy="106" rx="40" ry="14" fill="#78350f" fillOpacity="0.80" />
            <ellipse cx="1293" cy="101" rx="24" ry="10" fill="#78350f" fillOpacity="0.64" />
            <ellipse cx="1344" cy="103" rx="26" ry="11" fill="#78350f" fillOpacity="0.64" />
            {/* Small acacia mid */}
            <rect x="952" y="152" width="6" height="38" fill="#92400e" />
            <ellipse cx="955" cy="146" rx="26" ry="9" fill="#78350f" fillOpacity="0.68" />
          </svg>
        </div>
        <SafariWalkers />
        <SafariBirds />
      </>}

      {/* ── Rainforest ── */}
      {themeId === 'theme-rainforest' && <>
        <RainforestVines />
        <div className="theme-rainforest-mist" />
        <div className="theme-rainforest-canopy">
          <svg viewBox="0 0 1440 200" preserveAspectRatio="none" width="100%" height="100%">
            <defs>
              <linearGradient id="rf-mist" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#052e16" stopOpacity="0" />
                <stop offset="100%" stopColor="#16a34a" stopOpacity="0.20" />
              </linearGradient>
            </defs>
            {/* Deep ground */}
            <path d="M0 175 Q360 155 720 168 Q1080 180 1440 160 L1440 200 L0 200 Z"
              fill="#052e16" fillOpacity="0.58" />
            {/* Far leaves — muted */}
            <ellipse cx="200" cy="200" rx="55" ry="20" fill="#14532d" fillOpacity="0.45" transform="rotate(-14 200 200)" />
            <ellipse cx="550" cy="200" rx="50" ry="18" fill="#14532d" fillOpacity="0.40" transform="rotate(-8 550 200)"  />
            <ellipse cx="890" cy="200" rx="52" ry="19" fill="#14532d" fillOpacity="0.42" transform="rotate(10 890 200)"  />
            <ellipse cx="1240" cy="196" rx="58" ry="22" fill="#14532d" fillOpacity="0.48" transform="rotate(18 1240 196)" />
            {/* Mid leaves — medium green */}
            <ellipse cx="60"   cy="190" rx="76" ry="28" fill="#166534" fillOpacity="0.72" transform="rotate(-30 60 190)"   />
            <ellipse cx="38"   cy="198" rx="66" ry="24" fill="#166534" fillOpacity="0.62" transform="rotate(-20 38 198)"   />
            <ellipse cx="1382" cy="186" rx="82" ry="28" fill="#166534" fillOpacity="0.72" transform="rotate(25 1382 186)"  />
            <ellipse cx="1402" cy="200" rx="72" ry="26" fill="#166534" fillOpacity="0.62" transform="rotate(15 1402 200)"  />
            {/* Near leaves — bright, foreground */}
            <ellipse cx="-15"  cy="200" rx="55" ry="20" fill="#22c55e" fillOpacity="0.60" transform="rotate(-25 -15 200)"  />
            <ellipse cx="1455" cy="200" rx="55" ry="20" fill="#22c55e" fillOpacity="0.60" transform="rotate(25 1455 200)"  />
            <rect x="0" y="0" width="1440" height="200" fill="url(#rf-mist)" />
          </svg>
        </div>
        <RainforestRain seed={themeSeed} />
        <RainforestFireflies seed={themeSeed + 100} />
      </>}

      {/* ── Enchanted Castle ── */}
      {themeId === 'theme-enchanted-castle' && <>
        <div className="theme-castle-shimmer" />
        <div className="theme-castle-bg">
          <svg viewBox="0 0 1440 260" preserveAspectRatio="none" width="100%" height="100%">
            <defs>
              <linearGradient id="castle-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2e1065" stopOpacity="0" />
                <stop offset="100%" stopColor="#3b0764" stopOpacity="0.48" />
              </linearGradient>
              <radialGradient id="moon-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fef3c7" stopOpacity="0.55" />
                <stop offset="55%" stopColor="#fef9c3" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#fef9c3" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="gate-light" cx="50%" cy="100%" r="50%">
                <stop offset="0%" stopColor="#fcd34d" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#fcd34d" stopOpacity="0" />
              </radialGradient>
            </defs>

            <rect x="0" y="0" width="1440" height="260" fill="url(#castle-sky)" />

            {/* Full moon behind castle */}
            <circle cx="720" cy="52" r="72"  fill="url(#moon-glow)" className="castle-moon" />
            <circle cx="720" cy="52" r="44"  fill="#fefce8" fillOpacity="0.18" className="castle-moon" />

            {/* Stars */}
            <circle cx="280" cy="38"  r="2"   fill="#fcd34d" fillOpacity="0.88" />
            <circle cx="480" cy="20"  r="1.5" fill="#fcd34d" fillOpacity="0.76" />
            <circle cx="560" cy="42"  r="1"   fill="#e9d5ff" fillOpacity="0.70" />
            <circle cx="878" cy="18"  r="1"   fill="#e9d5ff" fillOpacity="0.70" />
            <circle cx="960" cy="40"  r="1.5" fill="#fcd34d" fillOpacity="0.76" />
            <circle cx="1060" cy="24" r="2"   fill="#fcd34d" fillOpacity="0.88" />
            <circle cx="1340" cy="18" r="2"   fill="#fcd34d" fillOpacity="0.85" />

            {/* Ground */}
            <path d="M0 232 Q720 216 1440 228 L1440 260 L0 260 Z"
              fill="#1e0a3c" fillOpacity="0.58" />

            {/* Left tower */}
            <rect x="116" y="94"  width="74" height="148" fill="#1e0a3c" fillOpacity="0.90" />
            <rect x="106" y="82"  width="18" height="30"  fill="#1e0a3c" fillOpacity="0.90" />
            <rect x="132" y="82"  width="18" height="30"  fill="#1e0a3c" fillOpacity="0.90" />
            <rect x="158" y="82"  width="18" height="30"  fill="#1e0a3c" fillOpacity="0.90" />
            <path d="M116 94 L153 52 L190 94 Z" fill="#4c1d95" fillOpacity="0.72" />
            <rect x="142" y="114" width="16" height="24" rx="8" fill="#7c3aed" fillOpacity="0.32" className="castle-window-glow" />

            {/* Right tower */}
            <rect x="1250" y="106" width="74" height="136" fill="#1e0a3c" fillOpacity="0.90" />
            <rect x="1240" y="94"  width="18" height="30"  fill="#1e0a3c" fillOpacity="0.90" />
            <rect x="1266" y="94"  width="18" height="30"  fill="#1e0a3c" fillOpacity="0.90" />
            <rect x="1292" y="94"  width="18" height="30"  fill="#1e0a3c" fillOpacity="0.90" />
            <path d="M1250 106 L1287 64 L1324 106 Z" fill="#4c1d95" fillOpacity="0.72" />
            <rect x="1278" y="126" width="16" height="24" rx="8" fill="#7c3aed" fillOpacity="0.32" className="castle-window-glow" />

            {/* Center wall + battlements */}
            <rect x="190" y="165" width="1060" height="82" fill="#1e0a3c" fillOpacity="0.75" />
            <rect x="190" y="153" width="22" height="22"   fill="#1e0a3c" fillOpacity="0.75" />
            <rect x="232" y="153" width="22" height="22"   fill="#1e0a3c" fillOpacity="0.75" />
            <rect x="1186" y="153" width="22" height="22"  fill="#1e0a3c" fillOpacity="0.75" />
            <rect x="1228" y="153" width="22" height="22"  fill="#1e0a3c" fillOpacity="0.75" />

            {/* Gate arch */}
            <path d="M658 260 L658 188 Q720 150 782 188 L782 260 Z" fill="#0f0520" fillOpacity="0.98" />
            {/* Gate glow from inside */}
            <ellipse cx="720" cy="228" rx="55" ry="30" fill="url(#gate-light)" />

            {/* Moat reflection */}
            <ellipse cx="720" cy="244" rx="440" ry="10" fill="rgba(139,92,246,0.12)" />
            <rect x="116" y="240" width="74" height="12" fill="#1e0a3c" fillOpacity="0.28" />
            <rect x="1250" y="240" width="74" height="10" fill="#1e0a3c" fillOpacity="0.28" />
            <path d="M280 244 Q480 240 720 246 Q960 252 1160 244" stroke="rgba(196,181,253,0.20)" strokeWidth="1" fill="none" />
          </svg>
        </div>
        <CastleWisps seed={themeSeed} />
        <CastleBats />
      </>}

      {/* ── Arctic ── */}
      {themeId === 'theme-arctic' && <>
        <div className="theme-arctic-aurora theme-arctic-aurora--1" />
        <div className="theme-arctic-aurora theme-arctic-aurora--2" />
        <div className="theme-arctic-aurora theme-arctic-aurora--3" />
        <ArcticScene />
        <div className="theme-arctic-ice" />
        <ArcticSnow seed={themeSeed} />
      </>}

      {/* ── Pirate ── */}
      {themeId === 'theme-pirate' && <>
        <div className="theme-pirate-fog theme-pirate-fog--1" />
        <div className="theme-pirate-fog theme-pirate-fog--2" />
        <PirateScene />
        <PirateWaves seed={themeSeed} />
      </>}

      {/* ── Candy ── */}
      {themeId === 'theme-candy' && <>
        <div className="theme-candy-rainbow" />
        <div className="theme-candy-glow" />
        <div className="theme-candy-dots" />
        <CandyBubbles seed={themeSeed} />
      </>}

      {/* Center shield */}
      <div className="theme-center-shield" />

      {/* Floating particles — parallax depth layers */}
      {particles.map(p => (
        <span key={p.id} className="theme-particle" style={{
          left: `${p.left}%`,
          fontSize: `${p.scale * 1.5}rem`,
          '--particle-duration': `${p.duration}s`,
          '--particle-delay': `${p.delay}s`,
          '--particle-scale': p.scale,
          '--drift-x': `${p.driftX}px`,
          '--drift-rotate': `${p.driftRotate}deg`,
        } as React.CSSProperties}>
          {p.emoji}
        </span>
      ))}
    </div>
  );
}
